/**
 * Chart Data Formatting Utilities
 * Converts Rails data to Chart.js format with proper formatting for different data types
 */

/**
 * Currency formatting utilities
 */
export const CurrencyFormatter = {
  /**
   * Format currency value for display
   * @param {number} value - The currency value in cents or dollars
   * @param {string} currency - Currency code (default: 'USD')
   * @param {boolean} fromCents - Whether the value is in cents (default: false)
   * @returns {string} Formatted currency string
   */
  format(value, currency = 'USD', fromCents = false) {
    if (value === null || value === undefined || isNaN(value)) {
      return '$0.00'
    }

    const amount = fromCents ? value / 100 : value

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  },

  /**
   * Format currency for chart tooltips (shorter format)
   * @param {number} value - The currency value
   * @param {boolean} fromCents - Whether the value is in cents
   * @returns {string} Formatted currency string
   */
  formatTooltip(value, fromCents = false) {
    if (value === null || value === undefined || isNaN(value)) {
      return '$0'
    }

    const amount = fromCents ? value / 100 : value

    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}K`
    } else {
      return `$${amount.toFixed(0)}`
    }
  },

  /**
   * Format currency for chart labels (compact format)
   * @param {number} value - The currency value
   * @param {boolean} fromCents - Whether the value is in cents
   * @returns {string} Formatted currency string
   */
  formatCompact(value, fromCents = false) {
    if (value === null || value === undefined || isNaN(value)) {
      return '$0'
    }

    const amount = fromCents ? value / 100 : value

    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(0)}M`
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`
    } else {
      return `$${amount.toFixed(0)}`
    }
  }
}

/**
 * Percentage formatting utilities
 */
export const PercentageFormatter = {
  /**
   * Format percentage value for display
   * @param {number} value - The percentage value (0-1 or 0-100)
   * @param {boolean} isDecimal - Whether the value is decimal (0-1) or percentage (0-100)
   * @param {number} decimals - Number of decimal places (default: 1)
   * @returns {string} Formatted percentage string
   */
  format(value, isDecimal = true, decimals = 1) {
    if (value === null || value === undefined || isNaN(value)) {
      return '0%'
    }

    const percentage = isDecimal ? value * 100 : value
    return `${percentage.toFixed(decimals)}%`
  },

  /**
   * Format percentage for chart tooltips
   * @param {number} value - The percentage value
   * @param {boolean} isDecimal - Whether the value is decimal
   * @returns {string} Formatted percentage string
   */
  formatTooltip(value, isDecimal = true) {
    return this.format(value, isDecimal, 1)
  },

  /**
   * Format percentage for chart labels (no decimals)
   * @param {number} value - The percentage value
   * @param {boolean} isDecimal - Whether the value is decimal
   * @returns {string} Formatted percentage string
   */
  formatCompact(value, isDecimal = true) {
    return this.format(value, isDecimal, 0)
  }
}

/**
 * Date formatting utilities
 */
export const DateFormatter = {
  /**
   * Format date for chart labels
   * @param {string|Date} date - The date to format
   * @param {string} format - Format type ('short', 'medium', 'long', 'month', 'day')
   * @returns {string} Formatted date string
   */
  format(date, format = 'short') {
    if (!date) return ''

    const dateObj = typeof date === 'string' ? new Date(date) : date

    if (isNaN(dateObj.getTime())) {
      return ''
    }

    const options = this.getFormatOptions(format)
    return new Intl.DateTimeFormat('en-US', options).format(dateObj)
  },

  /**
   * Get format options for Intl.DateTimeFormat
   * @param {string} format - Format type
   * @returns {object} Format options
   */
  getFormatOptions(format) {
    switch (format) {
      case 'short':
        return { month: 'short', day: 'numeric' }
      case 'medium':
        return { month: 'short', day: 'numeric', year: 'numeric' }
      case 'long':
        return { month: 'long', day: 'numeric', year: 'numeric' }
      case 'month':
        return { month: 'short' }
      case 'monthYear':
        return { month: 'short', year: 'numeric' }
      case 'day':
        return { weekday: 'short' }
      case 'dayMonth':
        return { weekday: 'short', month: 'short', day: 'numeric' }
      default:
        return { month: 'short', day: 'numeric' }
    }
  },

  /**
   * Format date range for chart titles
   * @param {string|Date} startDate - Start date
   * @param {string|Date} endDate - End date
   * @returns {string} Formatted date range string
   */
  formatRange(startDate, endDate) {
    if (!startDate || !endDate) return ''

    const start = typeof startDate === 'string' ? new Date(startDate) : startDate
    const end = typeof endDate === 'string' ? new Date(endDate) : endDate

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return ''
    }

    const startFormatted = this.format(start, 'medium')
    const endFormatted = this.format(end, 'medium')

    return `${startFormatted} - ${endFormatted}`
  },

  /**
   * Generate date labels for time series charts
   * @param {string|Date} startDate - Start date
   * @param {string|Date} endDate - End date
   * @param {string} interval - Interval ('day', 'week', 'month', 'quarter', 'year')
   * @returns {Array<string>} Array of formatted date labels
   */
  generateLabels(startDate, endDate, interval = 'month') {
    const start = typeof startDate === 'string' ? new Date(startDate) : startDate
    const end = typeof endDate === 'string' ? new Date(endDate) : endDate
    const labels = []

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return labels
    }

    let current = new Date(start)
    const endTime = end.getTime()

    while (current.getTime() <= endTime) {
      labels.push(this.format(current, this.getIntervalFormat(interval)))
      current = this.addInterval(current, interval)
    }

    return labels
  },

  /**
   * Get format for interval type
   * @param {string} interval - Interval type
   * @returns {string} Format string
   */
  getIntervalFormat(interval) {
    switch (interval) {
      case 'day':
        return 'dayMonth'
      case 'week':
        return 'short'
      case 'month':
        return 'month'
      case 'quarter':
        return 'monthYear'
      case 'year':
        return 'year'
      default:
        return 'short'
    }
  },

  /**
   * Add interval to date
   * @param {Date} date - Base date
   * @param {string} interval - Interval type
   * @returns {Date} New date with interval added
   */
  addInterval(date, interval) {
    const newDate = new Date(date)

    switch (interval) {
      case 'day':
        newDate.setDate(newDate.getDate() + 1)
        break
      case 'week':
        newDate.setDate(newDate.getDate() + 7)
        break
      case 'month':
        newDate.setMonth(newDate.getMonth() + 1)
        break
      case 'quarter':
        newDate.setMonth(newDate.getMonth() + 3)
        break
      case 'year':
        newDate.setFullYear(newDate.getFullYear() + 1)
        break
      default:
        newDate.setMonth(newDate.getMonth() + 1)
    }

    return newDate
  }
}

/**
 * Number formatting utilities
 */
export const NumberFormatter = {
  /**
   * Format number with appropriate units
   * @param {number} value - The number to format
   * @param {number} decimals - Number of decimal places
   * @returns {string} Formatted number string
   */
  format(value, decimals = 0) {
    if (value === null || value === undefined || isNaN(value)) {
      return '0'
    }

    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(decimals)}M`
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(decimals)}K`
    } else {
      return value.toFixed(decimals)
    }
  },

  /**
   * Format number for tooltips (with commas)
   * @param {number} value - The number to format
   * @returns {string} Formatted number string
   */
  formatTooltip(value) {
    if (value === null || value === undefined || isNaN(value)) {
      return '0'
    }

    return new Intl.NumberFormat('en-US').format(value)
  }
}

/**
 * Chart data conversion utilities
 */
export const ChartDataConverter = {
  /**
   * Convert Rails analytics data to Chart.js format
   * @param {object} railsData - Data from Rails analytics services
   * @param {string} chartType - Type of chart ('bar', 'line', 'doughnut', etc.)
   * @param {object} options - Conversion options
   * @returns {object} Chart.js compatible data object
   */
  convertToChartData(railsData, chartType, options = {}) {
    if (!railsData) {
      return { labels: [], datasets: [] }
    }

    switch (chartType) {
      case 'bar':
      case 'line':
        return this.convertToBarLineData(railsData, options)
      case 'doughnut':
      case 'pie':
        return this.convertToPieData(railsData, options)
      case 'radar':
        return this.convertToRadarData(railsData, options)
      default:
        return this.convertToBarLineData(railsData, options)
    }
  },

  /**
   * Convert data for bar/line charts
   * @param {object} railsData - Rails data
   * @param {object} options - Conversion options
   * @returns {object} Chart.js data
   */
  convertToBarLineData(railsData, options = {}) {
    const {
      labelKey = 'label',
      valueKey = 'value',
      datasetLabel = 'Data',
      formatCurrency = false,
      formatPercentage = false,
      fromCents = false
    } = options

    // Handle different data structures
    let labels = []
    let data = []

    if (Array.isArray(railsData)) {
      // Array of objects: [{ label: 'Jan', value: 100 }, ...]
      labels = railsData.map(item => item[labelKey] || item.label || item.name)
      data = railsData.map(item => {
        const value = item[valueKey] || item.value || item.count || 0
        return formatCurrency ? (fromCents ? value / 100 : value) : value
      })
    } else if (typeof railsData === 'object') {
      // Object with key-value pairs: { 'Jan': 100, 'Feb': 150, ... }
      labels = Object.keys(railsData)
      data = Object.values(railsData).map(value => {
        return formatCurrency ? (fromCents ? value / 100 : value) : value
      })
    }

    return {
      labels: labels,
      datasets: [{
        label: datasetLabel,
        data: data,
        tension: 0.4
      }]
    }
  },

  /**
   * Convert data for pie/doughnut charts
   * @param {object} railsData - Rails data
   * @param {object} options - Conversion options
   * @returns {object} Chart.js data
   */
  convertToPieData(railsData, options = {}) {
    const {
      labelKey = 'label',
      valueKey = 'value',
      formatCurrency = false,
      fromCents = false
    } = options

    let labels = []
    let data = []

    if (Array.isArray(railsData)) {
      labels = railsData.map(item => item[labelKey] || item.label || item.name)
      data = railsData.map(item => {
        const value = item[valueKey] || item.value || item.count || 0
        return formatCurrency ? (fromCents ? value / 100 : value) : value
      })
    } else if (typeof railsData === 'object') {
      labels = Object.keys(railsData)
      data = Object.values(railsData).map(value => {
        return formatCurrency ? (fromCents ? value / 100 : value) : value
      })
    }

    return {
      labels: labels,
      datasets: [{
        data: data
      }]
    }
  },

  /**
   * Convert data for radar charts
   * @param {object} railsData - Rails data
   * @param {object} options - Conversion options
   * @returns {object} Chart.js data
   */
  convertToRadarData(railsData, options = {}) {
    const {
      labelKey = 'label',
      valueKey = 'value',
      datasetLabel = 'Metrics'
    } = options

    let labels = []
    let data = []

    if (Array.isArray(railsData)) {
      labels = railsData.map(item => item[labelKey] || item.label || item.name)
      data = railsData.map(item => item[valueKey] || item.value || item.count || 0)
    } else if (typeof railsData === 'object') {
      labels = Object.keys(railsData)
      data = Object.values(railsData)
    }

    return {
      labels: labels,
      datasets: [{
        label: datasetLabel,
        data: data,
        fill: true,
        backgroundColor: 'rgba(2, 132, 199, 0.2)',
        borderColor: 'rgb(2, 132, 199)',
        pointBackgroundColor: 'rgb(2, 132, 199)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgb(2, 132, 199)'
      }]
    }
  },

  /**
   * Convert time series data for line charts
   * @param {Array} railsData - Array of time series data
   * @param {object} options - Conversion options
   * @returns {object} Chart.js data
   */
  convertTimeSeriesData(railsData, options = {}) {
    const {
      dateKey = 'date',
      valueKey = 'value',
      datasetLabel = 'Trend',
      dateFormat = 'short',
      formatCurrency = false,
      fromCents = false
    } = options

    if (!Array.isArray(railsData)) {
      return { labels: [], datasets: [] }
    }

    const labels = railsData.map(item => {
      const date = item[dateKey] || item.date || item.period
      return DateFormatter.format(date, dateFormat)
    })

    const data = railsData.map(item => {
      const value = item[valueKey] || item.value || item.count || 0
      return formatCurrency ? (fromCents ? value / 100 : value) : value
    })

    return {
      labels: labels,
      datasets: [{
        label: datasetLabel,
        data: data,
        fill: false,
        tension: 0.4
      }]
    }
  },

  /**
   * Convert multi-dataset data
   * @param {object} railsData - Rails data with multiple datasets
   * @param {object} options - Conversion options
   * @returns {object} Chart.js data
   */
  convertMultiDatasetData(railsData, options = {}) {
    const {
      labelsKey = 'labels',
      datasetsKey = 'datasets',
      formatCurrency = false,
      fromCents = false
    } = options

    if (!railsData || !railsData[labelsKey] || !railsData[datasetsKey]) {
      return { labels: [], datasets: [] }
    }

    const labels = railsData[labelsKey]
    const datasets = railsData[datasetsKey].map(dataset => ({
      ...dataset,
      data: dataset.data.map(value => {
        return formatCurrency ? (fromCents ? value / 100 : value) : value
      })
    }))

    return {
      labels: labels,
      datasets: datasets
    }
  }
}

/**
 * Chart tooltip callbacks
 */
export const TooltipCallbacks = {
  /**
   * Currency tooltip callback
   * @param {boolean} fromCents - Whether values are in cents
   * @returns {function} Tooltip callback function
   */
  currency(fromCents = false) {
    return {
      label: function(context) {
        const value = context.parsed.y || context.parsed
        const formatted = CurrencyFormatter.formatTooltip(value, fromCents)
        // Use context.label for pie/doughnut charts, dataset.label for others
        const label = context.label || context.dataset.label || ''
        return `${label}: ${formatted}`
      }
    }
  },

  /**
   * Percentage tooltip callback
   * @param {boolean} isDecimal - Whether values are decimal (0-1)
   * @returns {function} Tooltip callback function
   */
  percentage(isDecimal = true) {
    return {
      label: function(context) {
        const value = context.parsed.y || context.parsed
        const formatted = PercentageFormatter.formatTooltip(value, isDecimal)
        // Use context.label for pie/doughnut charts, dataset.label for others
        const label = context.label || context.dataset.label || ''
        return `${label}: ${formatted}`
      }
    }
  },

  /**
   * Number tooltip callback with formatting
   * @returns {function} Tooltip callback function
   */
  number() {
    return {
      label: function(context) {
        const value = context.parsed.y || context.parsed
        const formatted = NumberFormatter.formatTooltip(value)
        // Use context.label for pie/doughnut charts, dataset.label for others
        const label = context.label || context.dataset.label || ''
        return `${label}: ${formatted}`
      }
    }
  },

  /**
   * Custom tooltip callback for drill-down functionality
   * @param {string} drillDownText - Text to show for drill-down hint
   * @returns {function} Tooltip callback function
   */
  drillDown(drillDownText = 'Click to view details') {
    return {
      afterLabel: function() {
        return drillDownText
      }
    }
  }
}

/**
 * Export all utilities
 */
export default {
  CurrencyFormatter,
  PercentageFormatter,
  DateFormatter,
  NumberFormatter,
  ChartDataConverter,
  TooltipCallbacks
}
